import React, { useState } from "react"
import PropTypes from "prop-types"
import moment from "moment"
import {
  Button,
  Card,
  CardBody,
  CardTitle,
  Col,
  Collapse,
  Row,
  Table,
} from "reactstrap"

const DAY_ORDER = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"]

const formatLabel = value =>
  value ? `${value.charAt(0).toUpperCase()}${value.slice(1)}` : ""

const hasMetric = value => value !== undefined && value !== null && value !== ""

const formatTime = (value, fallback) => {
  if (!value) {
    return fallback
  }

  const parsedTime = moment(value, [moment.ISO_8601, "HH:mm", "H:mm"], true)
  return parsedTime.isValid() ? parsedTime.format("HH:mm") : fallback
}

const getAvailabilityEntries = availability =>
  Object.entries(availability || {})
    .map(([day, interval]) => ({
      day,
      dayIndex: DAY_ORDER.indexOf(day.toLowerCase().slice(0, 3)),
      endTime: formatTime(interval?.endDate, "22:00"),
      startTime: formatTime(interval?.startDate, "08:00"),
    }))
    .filter(({ dayIndex }) => dayIndex !== -1)
    .sort((first, second) => first.dayIndex - second.dayIndex)

const formatRewardValue = (amount, currency) =>
  `${amount ?? 0}${currency ? ` ${currency}` : ""}`

const SummaryMetric = ({ helper, label, value }) => (
  <div className="bg-light rounded px-3 py-2 h-100">
    <div className="text-muted font-size-12 mb-1">{label}</div>
    <div className="font-weight-semibold font-size-15">{value}</div>
    {helper && <div className="text-muted font-size-12 mt-1">{helper}</div>}
  </div>
)

SummaryMetric.propTypes = {
  helper: PropTypes.node,
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
}

SummaryMetric.defaultProps = {
  helper: "",
}

// PRD 16 LR16 (LR10 verdict U-B): the availability and the typical hours are a player's (`showAvailability`)
export const ProfileOverviewCard = ({
  availability,
  showAvailability,
  currency,
  invitationCount,
  invitationLimit,
  invitationRewards,
  networkSize,
  ownRewards,
  totalRewards,
}) => {
  const [isExpanded, setIsExpanded] = useState(false)
  const entries = getAvailabilityEntries(availability)
  const scheduleCount = new Set(
    entries.map(({ endTime, startTime }) => `${startTime}-${endTime}`)
  ).size
  const timeSummary = !entries.length
    ? "—"
    : scheduleCount === 1
    ? `${entries[0].startTime}–${entries[0].endTime}`
    : "Hours vary"

  return (
    <Card>
      <CardBody className="py-3">
        <Row className="mb-n3 mb-xl-0">
          <Col xs="6" md="4" xl="2" className="mb-3 mb-xl-0">
            <SummaryMetric
              label="Total rewards"
              value={formatRewardValue(totalRewards, currency)}
            />
          </Col>
          <Col xs="6" md="4" xl="2" className="mb-3 mb-xl-0">
            <SummaryMetric
              label="Own rewards"
              value={formatRewardValue(ownRewards, currency)}
            />
          </Col>
          <Col xs="6" md="4" xl="2" className="mb-3 mb-xl-0">
            <SummaryMetric
              helper={`${invitationCount ?? 0} of ${
                invitationLimit ?? "—"
              } invitations`}
              label="Invitation rewards"
              value={formatRewardValue(invitationRewards, currency)}
            />
          </Col>
          <Col xs="6" md="4" xl="2" className="mb-3 mb-xl-0">
            <SummaryMetric
              label="Network size"
              value={networkSize == null ? "Unavailable" : `${networkSize} players`}
            />
          </Col>
          {showAvailability && <>
          <Col xs="6" md="4" xl="2" className="mb-3 mb-xl-0">
            <SummaryMetric
              label="Availability"
              value={entries.length ? `${entries.length} of 7 days` : "Not set"}
            />
          </Col>
          <Col xs="6" md="4" xl="2" className="mb-3 mb-xl-0">
            <SummaryMetric
              helper={
                entries.length ? (
                  <Button
                    aria-label={
                      isExpanded
                        ? "Hide weekly schedule"
                        : "Show weekly schedule"
                    }
                    aria-controls="player-availability-schedule"
                    aria-expanded={isExpanded}
                    className="p-0 font-size-12 font-weight-medium"
                    color="link"
                    onClick={() => setIsExpanded(current => !current)}
                  >
                    {isExpanded ? "Hide schedule" : "View schedule"}
                    <i
                      aria-hidden="true"
                      className={`bx bx-chevron-${
                        isExpanded ? "up" : "down"
                      } ml-1`}
                    />
                  </Button>
                ) : null
              }
              label="Typical hours"
              value={timeSummary}
            />
          </Col>
          </>}
        </Row>

        <Collapse isOpen={showAvailability && isExpanded}>
          {showAvailability && isExpanded && (
            <div
              className="border-top mt-3 pt-3 table-responsive"
              id="player-availability-schedule"
            >
              <Table
                aria-label="Weekly availability"
                className="table-nowrap table-sm mb-0"
              >
                <thead>
                  <tr>
                    <th>Day</th>
                    <th>Available from</th>
                    <th>Until</th>
                  </tr>
                </thead>
                <tbody>
                  {entries.map(({ day, endTime, startTime }) => (
                    <tr key={day}>
                      <th scope="row">{formatLabel(day.slice(0, 3))}</th>
                      <td>{startTime}</td>
                      <td>{endTime}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}
        </Collapse>
      </CardBody>
    </Card>
  )
}

ProfileOverviewCard.propTypes = {
  availability: PropTypes.object,
  showAvailability: PropTypes.bool,
  currency: PropTypes.string,
  invitationCount: PropTypes.number,
  invitationLimit: PropTypes.number,
  invitationRewards: PropTypes.number,
  networkSize: PropTypes.number,
  ownRewards: PropTypes.number,
  totalRewards: PropTypes.number,
}

ProfileOverviewCard.defaultProps = {
  availability: {},
  showAvailability: true,
  currency: "",
  invitationCount: 0,
  invitationLimit: undefined,
  invitationRewards: 0,
  networkSize: null,
  ownRewards: 0,
  totalRewards: 0,
}

const SportFormatSummary = ({ format, values }) => {
  const level = values?.gameLevel
  const rank = values?.rank
  const points = values?.points

  return (
    <div
      className="bg-light rounded px-3 py-2 h-100"
      data-testid={`${format.sport}-${format.type}`}
    >
      <div className="d-flex align-items-center justify-content-between mb-2">
        <span className="font-weight-semibold">{format.label}</span>
        <span
          className={`badge font-size-12 ${
            hasMetric(level) ? "badge-soft-primary" : "badge-soft-secondary"
          }`}
        >
          {hasMetric(level) ? `Level ${level}` : "Not set"}
        </span>
      </div>
      <div className="d-flex align-items-start">
        <div className="mr-4">
          <div className="text-muted font-size-12">Rank</div>
          <div className="font-weight-semibold">
            {hasMetric(rank) ? `#${rank}` : "Not set"}
          </div>
        </div>
        <div>
          <div className="text-muted font-size-12">Points</div>
          <div className="font-weight-semibold">
            {hasMetric(points) ? points : "Not set"}
          </div>
        </div>
      </div>
    </div>
  )
}

SportFormatSummary.propTypes = {
  format: PropTypes.shape({
    label: PropTypes.string.isRequired,
    sport: PropTypes.string.isRequired,
    type: PropTypes.string.isRequired,
  }).isRequired,
  values: PropTypes.object,
}

SportFormatSummary.defaultProps = {
  values: {},
}

export const SportsSummaryCard = ({ activeSports, sports }) => (
  <Card>
    <CardBody>
      <CardTitle className="mb-1">Game Levels, Rankings &amp; Points</CardTitle>
      <p className="text-muted mb-3">
        Competitive profile by sport and match format
      </p>

      {activeSports.length ? (
        activeSports.map((sport, index) => (
          <div
            className={`border rounded p-3 ${
              index < activeSports.length - 1 ? "mb-3" : ""
            }`}
            key={sport}
          >
            <Row className="align-items-stretch">
              <Col lg="2" className="d-flex align-items-center mb-3 mb-lg-0">
                <div>
                  <h5 className="mb-1">{formatLabel(sport)}</h5>
                  <span className="text-muted font-size-12">Current values</span>
                </div>
              </Col>
              <Col lg="5" className="mb-3 mb-lg-0">
                <SportFormatSummary
                  format={{
                    label: "Singles",
                    sport,
                    type: "singles",
                  }}
                  values={sports?.[sport]?.singles}
                />
              </Col>
              <Col lg="5">
                <SportFormatSummary
                  format={{
                    label: "Doubles",
                    sport,
                    type: "doubles",
                  }}
                  values={sports?.[sport]?.doubles}
                />
              </Col>
            </Row>
          </div>
        ))
      ) : (
        <p className="text-muted mb-0">No sports are configured.</p>
      )}
    </CardBody>
  </Card>
)

SportsSummaryCard.propTypes = {
  activeSports: PropTypes.arrayOf(PropTypes.string),
  sports: PropTypes.object,
}

SportsSummaryCard.defaultProps = {
  activeSports: [],
  sports: {},
}
